import React, { createContext, useContext, useEffect, useState, useRef, useMemo } from 'react';
import { supabase } from '../lib/supabase';
import { userService } from '../services/userService';
import { useLocation } from 'react-router-dom';

import { decodeJwtPayload } from '../utils/jwt';

const AuthContext = createContext({});

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [profileLoading, setProfileLoading] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isSeller, setIsSeller] = useState(false);
  const [isSupplier, setIsSupplier] = useState(false);
  const [isManufacturer, setIsManufacturer] = useState(false);
  const [isRepairShop, setIsRepairShop] = useState(false);
  const [isWrapShop, setIsWrapShop] = useState(false);
  const [isEnterprise, setIsEnterprise] = useState(false);
  const [repairTier, setRepairTier] = useState(null); // NEW: Enforce RBAC plans
  const [wrapTier, setWrapTier] = useState(null); // NEW: Enforce RBAC plans
  const [sellerTier, setSellerTier] = useState(null); // NEW: Enforce RBAC plans
  const [initialCheckComplete, setInitialCheckComplete] = useState(false); // New state
  const initialSessionCheckedRef = useRef(false); // New Ref to track initial check status
  const userRef = useRef(user); // Ref to hold current user value for comparison in listener
  const location = useLocation();
  const sessionStartTimeRef = useRef(new Date());

  useEffect(() => {
    userRef.current = user;
  }, [user]);

  // Function to fetch user profile and set states
  const fetchUserProfile = async (currentUser) => {
    // console.log('AuthContext: fetchUserProfile called with currentUser:', currentUser);
    if (!currentUser) {
      // console.log('AuthContext: No currentUser, resetting profile states.');
      if (userProfile !== null) setUserProfile(null);
      if (isAdmin !== false) setIsAdmin(false);
      if (isSeller !== false) setIsSeller(false);
      if (isSupplier !== false) setIsSupplier(false);
      if (profileLoading !== false) setProfileLoading(false);
      return;
    }

    if (profileLoading === false) { // Only set profileLoading to true if it's currently false
        setProfileLoading(true);
    }
    // console.log('AuthContext: Fetching user profile for user ID:', currentUser.id);
    try {
      // Step 1: Fetch user data
      // We query without sessions_revoked_at first to ensure compatibility if column is missing
      let { data: userData, error: userError } = await supabase
        .from('users')
        .select('id, email, first_name, last_name, location, avatar_url, created_at, raw_user_meta_data')
        .eq('id', currentUser.id)
        .maybeSingle();
      
      // Auto-create profile if missing
      if (!userData && !userError) {
        const { data: newProfile, error: insertError } = await supabase
          .from('users')
          .insert({
            id: currentUser.id,
            email: currentUser.email,
            first_name: currentUser.user_metadata?.first_name || '',
            last_name: currentUser.user_metadata?.last_name || '',
            created_at: new Date().toISOString()
          })
          .select('id, email, first_name, last_name, location, avatar_url, created_at, raw_user_meta_data')
          .maybeSingle();
          
        if (!insertError) {
          userData = newProfile;
        } else {
          console.error('AuthContext: Error creating user profile:', insertError);
        }
      }

      if (userError && userError.code !== 'PGRST116') {
        console.error('AuthContext: Error fetching user data:', userError);
        if (userProfile !== null) setUserProfile(null);
        if (isAdmin !== false) setIsAdmin(false);
        if (isSeller !== false) setIsSeller(false);
        if (isSupplier !== false) setIsSupplier(false);
        if (isManufacturer !== false) setIsManufacturer(false);
      } else if (userData) {
        // FORCE LOGOUT CHECK (Optional column check)
        try {
          const { data: revocationData } = await supabase
            .from('users')
            .select('sessions_revoked_at')
            .eq('id', currentUser.id)
            .maybeSingle();

          if (revocationData?.sessions_revoked_at) {
              const revokedAt = new Date(revocationData.sessions_revoked_at).getTime();
              
              const { data: { session } } = await supabase.auth.getSession();
              let sessionStart = sessionStartTimeRef.current.getTime();
              if (session?.access_token) {
                const payload = decodeJwtPayload(session.access_token);
                if (payload?.iat) sessionStart = payload.iat * 1000;
              }

              if (revokedAt > sessionStart) {
                  console.warn('AuthContext: Session revoked. Logging out...');
                  await supabase.auth.signOut();
                  window.location.href = '/signin?revoked=true';
                  return;
              }
          }
        } catch (revErr) {
          // Silent fail if column doesn't exist
        }

        let fullProfile = { ...userData };
        let isAdminUser = false;
        let isSellerUser = false;
        let isSupplierUser = false;
        let isManufacturerUser = false;
        let isRepairShopUser = false;
        let isWrapShopUser = false;
        let isEnterpriseUser = false;
        let userPermissions = new Set();
        let userRoles = [];

        // Step 2: Fetch all roles for this user
        const { data: roleAssignments, error: roleAssignmentError } = await supabase
          .from('user_roles')
          .select('roles(id, name)')
          .eq('user_id', currentUser.id);

        if (roleAssignmentError) {
          console.error('AuthContext: Error fetching user roles:', roleAssignmentError);
        } else if (roleAssignments) {
          userRoles = roleAssignments.map(ra => ra.roles).filter(Boolean);
          fullProfile.user_roles = userRoles;
          isAdminUser = userRoles.some(r => r.name === 'Admin');
          isSellerUser = userRoles.some(r => r.name === 'Seller');
          isSupplierUser = userRoles.some(r => r.name === 'Supplier');
          isManufacturerUser = userRoles.some(r => r.name === 'Manufacturer');
          isRepairShopUser = userRoles.some(r => r.name === 'Repair Shop');
          isWrapShopUser = userRoles.some(r => r.name === 'Wrap Shop');
          isEnterpriseUser = userRoles.some(r => r.name === 'Enterprise');

          // Step 3: Fetch all permissions for Admin users
          if (isAdminUser) {
            try {
              const roleIds = userRoles.map(r => r.id);
              const { data: permissionData } = await supabase
                .from('role_permissions')
                .select('permissions (name)')
                .in('role_id', roleIds);

              if (permissionData) {
                permissionData.forEach(p => {
                  if (p.permissions?.name) userPermissions.add(p.permissions.name);
                });
              }
            } catch (e) {
              console.warn('Could not fetch role_permissions', e);
            }


          }
        }
        
        fullProfile.permissions = Array.from(userPermissions);
        
        // Only update state if values have actually changed
        let activeRepairTier = null;
        let activeWrapTier = null;
        let activeSellerTier = null;
        
        if (isSellerUser || isRepairShopUser || isWrapShopUser) {
          try {
            const { data: sellerAcc } = await supabase
              .from('seller_accounts')
              .select('tier, repair_tier, wrap_tier')
              .eq('user_id', currentUser.id)
              .maybeSingle();
              
            if (sellerAcc) {
              activeRepairTier = sellerAcc.repair_tier;
              activeWrapTier = sellerAcc.wrap_tier;
              activeSellerTier = sellerAcc.tier;
            }
          } catch (e) {
            console.warn('Could not load seller_accounts tiers in auth:', e);
          }
        }

        setRepairTier(activeRepairTier);
        setWrapTier(activeWrapTier);
        setSellerTier(activeSellerTier);

        if (JSON.stringify(userProfile) !== JSON.stringify(fullProfile)) {
            setUserProfile(fullProfile);
        }
        if (isAdmin !== isAdminUser) {
            setIsAdmin(isAdminUser);
        }
        if (isSeller !== isSellerUser) {
            setIsSeller(isSellerUser);
        }
        if (isSupplier !== isSupplierUser) {
            setIsSupplier(isSupplierUser);
        }
        if (isManufacturer !== isManufacturerUser) {
            setIsManufacturer(isManufacturerUser);
        }
        if (isRepairShop !== isRepairShopUser) {
            setIsRepairShop(isRepairShopUser);
        }
        if (isWrapShop !== isWrapShopUser) {
            setIsWrapShop(isWrapShopUser);
        }
        if (isEnterprise !== isEnterpriseUser) {
            setIsEnterprise(isEnterpriseUser);
        }
        // console.log('AuthContext: UserProfile and role states updated.');

      } else { // User data is null or undefined
        console.warn('AuthContext: User data is null or undefined for user:', currentUser.id);
        if (userProfile !== null) setUserProfile(null);
        if (isAdmin !== false) setIsAdmin(false);
        if (isSeller !== false) setIsSeller(false);
        if (isSupplier !== false) setIsSupplier(false);
        if (isManufacturer !== false) setIsManufacturer(false);
        if (isRepairShop !== false) setIsRepairShop(false);
        if (isWrapShop !== false) setIsWrapShop(false);
        if (isEnterprise !== false) setIsEnterprise(false);
      }
    } catch (error) {
      console.error('AuthContext: General error in fetchUserProfile:', error);
      if (userProfile !== null) setUserProfile(null);
      if (isAdmin !== false) setIsAdmin(false);
      if (isSeller !== false) setIsSeller(false);
      if (isSupplier !== false) setIsSupplier(false);
      if (isManufacturer !== false) setIsManufacturer(false);
      if (isRepairShop !== false) setIsRepairShop(false);
      if (isWrapShop !== false) setIsWrapShop(false);
      if (isEnterprise !== false) setIsEnterprise(false);
    } finally {
      if (profileLoading !== false) setProfileLoading(false); // Ensure profileLoading is false
      // console.log('AuthContext: fetchUserProfile completed. profileLoading set to false.');
    }
  };

  // Expose fetchUserProfile as refreshUserProfile
  const refreshUserProfile = async () => {
    await fetchUserProfile(user);
  };

  // NEW: Function to manually refresh user data (e.g. after metadata update)
  const refreshUser = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (session?.user) {
        setUser(session.user);
      }
  };

  // REAL-TIME LISTENER for the current user's profile
  // This handles instant logout when Admin clicks "Force Logout"
  useEffect(() => {
    let profileSubscription = null;
    let checkInterval = null;

    const performRevocationCheck = async (currentUserId) => {
      try {
        const { data: userData } = await supabase
          .from('users')
          .select('sessions_revoked_at')
          .eq('id', currentUserId)
          .maybeSingle();

        if (userData?.sessions_revoked_at) {
          const revokedAt = new Date(userData.sessions_revoked_at).getTime();
          
          const { data: { session } } = await supabase.auth.getSession();
          let sessionStart = sessionStartTimeRef.current.getTime();
          if (session?.access_token) {
            const payload = decodeJwtPayload(session.access_token);
            if (payload?.iat) sessionStart = payload.iat * 1000;
          }

          if (revokedAt > sessionStart) {
            console.warn('AuthContext: Session revoked. Logging out...');
            if (checkInterval) clearInterval(checkInterval);
            await supabase.auth.signOut();
            window.location.href = '/signin?revoked=true';
          }
        }
      } catch (err) {}
    };

    const setupProfileListener = () => {
      if (!user?.id) return;

      if (profileSubscription) {
        supabase.removeChannel(profileSubscription);
        profileSubscription = null;
      }

      // 1. Real-time subscription
      profileSubscription = supabase
        .channel(`public:users:id=eq.${user.id}`)
        .on('postgres_changes', { 
          event: 'UPDATE', 
          schema: 'public', 
          table: 'users',
          filter: `id=eq.${user.id}`
        }, async (payload) => {
          if (payload.new?.sessions_revoked_at) {
            const revokedAt = new Date(payload.new.sessions_revoked_at).getTime();
            
            const { data: { session } } = await supabase.auth.getSession();
            let sessionStart = sessionStartTimeRef.current.getTime();
            if (session?.access_token) {
              const tokenPayload = decodeJwtPayload(session.access_token);
              if (tokenPayload?.iat) sessionStart = tokenPayload.iat * 1000;
            }

            if (revokedAt > sessionStart) {
              await supabase.auth.signOut();
              window.location.href = '/signin?revoked=true';
            }
          }
        })
        .subscribe();
    };

    setupProfileListener();

    // Clean teardown when page enters Back-Forward Cache (bfcache)
    const handlePageHide = () => {
      try {
        if (profileSubscription) {
          supabase.removeChannel(profileSubscription);
          profileSubscription = null;
        }
        if (supabase?.realtime?.isConnected?.()) {
          supabase.realtime.disconnect();
        }
      } catch (e) {}
    };

    const handlePageShow = (e) => {
      if (e.persisted) {
        try {
          supabase?.realtime?.connect();
        } catch (e) {}
        setupProfileListener();
      }
    };

    window.addEventListener('pagehide', handlePageHide);
    window.addEventListener('freeze', handlePageHide);
    window.addEventListener('pageshow', handlePageShow);
    window.addEventListener('resume', handlePageShow);

    return () => {
      window.removeEventListener('pagehide', handlePageHide);
      window.removeEventListener('freeze', handlePageHide);
      window.removeEventListener('pageshow', handlePageShow);
      window.removeEventListener('resume', handlePageShow);
      if (profileSubscription) {
        supabase.removeChannel(profileSubscription);
        profileSubscription = null;
      }
    };
  }, [user?.id]);

  // Effect for initial session check and profile fetch
  useEffect(() => {
    const checkInitialSession = async () => {
      const { data: { session } } = await supabase.auth.getSession();

      if (session) {
          sessionStartTimeRef.current = new Date();
      }

      setUser(session?.user ?? null);
      setInitialCheckComplete(true); 
      setLoading(false); 
      initialSessionCheckedRef.current = true; 
    };
    checkInitialSession();

    // Set up auth state change listener
    const { data: authListener } = supabase.auth.onAuthStateChange(
      (event, session) => {
        if (initialSessionCheckedRef.current && event === 'INITIAL_SESSION' && userRef.current?.id === session?.user?.id) {
            return;
        }

        if (userRef.current?.id === session?.user?.id && userRef.current !== null && session?.user !== null) {
          return;
        }

        if (event === 'SIGNED_IN') {
            sessionStartTimeRef.current = new Date();
        }

        setUser(session?.user ?? null);
      }
    );

    return () => {
      authListener.subscription.unsubscribe();
    };
  }, []); 
 // Run only once on mount

  // Effect to fetch/update profile whenever 'user' changes
  useEffect(() => {
    // console.log('AuthContext: user change useEffect triggered. Current user ID:', user?.id || 'N/A', 'initialCheckComplete:', initialCheckComplete);
    if (initialCheckComplete) { // Only fetch profile once initial auth check is done
        fetchUserProfile(user);
    }
  }, [user, initialCheckComplete]); // Re-run whenever user object changes or initial check completes

  const value = useMemo(() => ({
    user,
    userProfile,
    isAdmin,
    isSeller,
    isSupplier,
    isManufacturer,
    isRepairShop,
    isWrapShop,
    isEnterprise,
    isShop: isRepairShop || isWrapShop,
    repairTier,
    wrapTier,
    sellerTier,
    loading,
    profileLoading,
    signIn: async (email, password) => {
      return supabase.auth.signInWithPassword({ email, password });
    },
    signOut: () => supabase.auth.signOut(),
    signUp: (email, password, options) => supabase.auth.signUp({ email, password, options }),
    isAuthenticated: !!user && initialCheckComplete && !loading,
    initialCheckComplete,
    refreshUserProfile,
    refreshUser, // Expose refreshUser for session refresh
  }), [user, userProfile, isAdmin, isSeller, isSupplier, isManufacturer, isRepairShop, isWrapShop, isEnterprise, repairTier, wrapTier, sellerTier, loading, profileLoading, initialCheckComplete]);

  // console.log('AuthContext: Provided value:', value);
  // console.log('AuthContext: Rendering AuthContext.Provider with isAuthenticated:', value.isAuthenticated, 'loading:', value.loading, 'profileLoading:', value.profileLoading, 'user:', !!value.user);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}